import { Opportunity, Requirement, DraftSection, FormatRule, RequiredForm } from "../types";

export interface CompiledProposal {
  opportunity: Opportunity;
  requirements: Requirement[];
  draftSections: DraftSection[];
  formatRules: FormatRule[];
  requiredForms: RequiredForm[];
}

/**
 * Compiles a proposal document into standard Markdown
 */
export function generateMarkdownProposal(compiled: CompiledProposal): string {
  const { opportunity, requirements, draftSections, formatRules, requiredForms } = compiled;

  let md = `# Proposal Response: ${opportunity.title}\n\n`;
  md += `**Client:** ${opportunity.client}\n`;
  md += `**Submission Due Date:** ${opportunity.dueDate || "Not specified"}\n`;
  md += `**Proposal Lead:** ${opportunity.owner || "Multilingual Connections"}`;
  if (opportunity.ownerEmail) {
    md += ` (${opportunity.ownerEmail})`;
  }
  md += `\n**Prepared by:** Multilingual Connections (MLC) — Certified WBE\n\n`;

  // 1. Audit unapproved sections & placeholders
  const unapproved = draftSections.filter((s) => s.status !== "approved");
  const allPlaceholders: Array<{ sectionTitle: string; item: string }> = [];

  draftSections.forEach((sec) => {
    const matches = sec.content.match(/\[ACTION NEEDED:[^\]]+\]/gi);
    if (matches) {
      matches.forEach((m) => allPlaceholders.push({ sectionTitle: sec.title, item: m }));
    }
  });

  if (unapproved.length > 0 || allPlaceholders.length > 0) {
    md += `> [!WARNING] PRE-SUBMISSION AUDIT WARNINGS\n`;
    if (unapproved.length > 0) {
      md += `> - **Unapproved Sections (${unapproved.length}):** ${unapproved.map((s) => s.title).join(", ")}\n`;
    }
    if (allPlaceholders.length > 0) {
      md += `> - **Unresolved Action Items (${allPlaceholders.length}):**\n`;
      allPlaceholders.forEach((p) => {
        md += `>   * *${p.sectionTitle}:* ${p.item}\n`;
      });
    }
    md += `\n---\n\n`;
  }

  // 2. Drafted Response Sections
  md += `## 1. Technical & Narrative Responses\n\n`;

  // Sort sections according to requirements order
  const reqMap = new Map<string, Requirement>();
  requirements.forEach((r) => reqMap.set(r.id, r));

  const sortedSections = [...draftSections].sort((a, b) => {
    const reqA = reqMap.get(a.requirementId);
    const reqB = reqMap.get(b.requirementId);
    const refA = reqA?.sectionRef || a.title;
    const refB = reqB?.sectionRef || b.title;
    return refA.localeCompare(refB, undefined, { numeric: true });
  });

  if (sortedSections.length === 0) {
    md += `*(No draft response sections have been created yet.)*\n\n`;
  } else {
    sortedSections.forEach((sec) => {
      const req = reqMap.get(sec.requirementId);
      const ref = req?.sectionRef ? `${req.sectionRef} ` : "";
      md += `### ${ref}${sec.title}\n\n`;
      if (req?.prompt) {
        md += `*RFP Requirement:* ${req.prompt}\n\n`;
      }
      md += `${sec.content}\n\n`;
    });
  }

  // 3. Required Forms & Attachments Table
  if (requiredForms.length > 0) {
    md += `## 2. Required Forms & Compliance Attachments\n\n`;
    md += `| Form Name | Mandatory | Status |\n`;
    md += `| :--- | :--- | :--- |\n`;
    requiredForms.forEach((form) => {
      const isMandatory = form.mandatory ? "Yes" : "Optional";
      const statusLabel = form.status.replace(/_/g, " ").toUpperCase();
      md += `| ${form.formName} | ${isMandatory} | ${statusLabel} |\n`;
    });
    md += `\n`;
  }

  // 4. Format & Submission Rules
  if (formatRules.length > 0) {
    md += `## 3. Format & Submission Compliance\n\n`;
    md += `| Rule Type | Requirement | Compliant |\n`;
    md += `| :--- | :--- | :--- |\n`;
    formatRules.forEach((rule) => {
      const typeLabel = rule.ruleType.replace(/_/g, " ").toUpperCase();
      const compliantLabel = rule.isCompliant ? "Compliant" : "Review Needed";
      md += `| ${typeLabel} | ${rule.requirement} | ${compliantLabel} |\n`;
    });
    md += `\n`;
  }

  return md;
}

/**
 * Compiles a proposal into clean, styled HTML optimized for Google Docs and Microsoft Word paste
 */
export function generateGoogleDocsHtml(compiled: CompiledProposal): string {
  const { opportunity, requirements, draftSections, formatRules, requiredForms } = compiled;

  const unapproved = draftSections.filter((s) => s.status !== "approved");
  const allPlaceholders: Array<{ sectionTitle: string; item: string }> = [];

  draftSections.forEach((sec) => {
    const matches = sec.content.match(/\[ACTION NEEDED:[^\]]+\]/gi);
    if (matches) {
      matches.forEach((m) => allPlaceholders.push({ sectionTitle: sec.title, item: m }));
    }
  });

  const reqMap = new Map<string, Requirement>();
  requirements.forEach((r) => reqMap.set(r.id, r));

  const sortedSections = [...draftSections].sort((a, b) => {
    const reqA = reqMap.get(a.requirementId);
    const reqB = reqMap.get(b.requirementId);
    const refA = reqA?.sectionRef || a.title;
    const refB = reqB?.sectionRef || b.title;
    return refA.localeCompare(refB, undefined, { numeric: true });
  });

  let html = `<div style="font-family: Arial, Helvetica, sans-serif; font-size: 11pt; line-height: 1.6; color: #1e293b; max-width: 800px; margin: 0 auto;">`;

  // Header Title Block
  html += `
  <div style="border-bottom: 2px solid #2ba5b5; padding-bottom: 12px; margin-bottom: 24px;">
    <h1 style="color: #0f172a; font-size: 22pt; margin: 0 0 8px 0; font-weight: 700;">${opportunity.title}</h1>
    <div style="font-size: 11pt; color: #475569;">
      <strong>Client:</strong> ${opportunity.client} &nbsp;|&nbsp;
      <strong>Due Date:</strong> ${opportunity.dueDate || "Not specified"} &nbsp;|&nbsp;
      <strong>Lead:</strong> ${opportunity.owner || "Multilingual Connections"}
    </div>
    <div style="font-size: 10pt; color: #64748b; margin-top: 4px;">
      Prepared by Multilingual Connections (MLC) — Certified Women's Business Enterprise (WBE)
    </div>
  </div>`;

  // Warning Banners
  if (unapproved.length > 0) {
    html += `
    <div style="background-color: #fffbeb; border: 1px solid #f59e0b; border-radius: 6px; padding: 12px 16px; margin-bottom: 16px; color: #92400e;">
      <strong style="font-size: 11pt;">Notice: ${unapproved.length} section(s) are currently in Draft or Review status:</strong>
      <ul style="margin: 6px 0 0 0; padding-left: 20px;">
        ${unapproved.map((u) => `<li>${u.title} (Status: ${u.status.toUpperCase()})</li>`).join("")}
      </ul>
    </div>`;
  }

  if (allPlaceholders.length > 0) {
    html += `
    <div style="background-color: #fef2f2; border: 1px solid #ef4444; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px; color: #991b1b;">
      <strong style="font-size: 11pt;">Action Items Required: ${allPlaceholders.length} placeholder(s) need completion:</strong>
      <ul style="margin: 6px 0 0 0; padding-left: 20px;">
        ${allPlaceholders.map((p) => `<li><strong>${p.sectionTitle}:</strong> ${p.item}</li>`).join("")}
      </ul>
    </div>`;
  }

  // Section Responses
  html += `<h2 style="color: #0f172a; font-size: 16pt; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; margin-top: 28px;">1. Technical & Narrative Proposal Sections</h2>`;

  if (sortedSections.length === 0) {
    html += `<p style="font-style: italic; color: #64748b;">(No draft sections created yet.)</p>`;
  } else {
    sortedSections.forEach((sec) => {
      const req = reqMap.get(sec.requirementId);
      const ref = req?.sectionRef ? `${req.sectionRef} ` : "";
      html += `
      <div style="margin-top: 24px;">
        <h3 style="color: #1e293b; font-size: 13pt; margin-bottom: 6px; font-weight: 600;">${ref}${sec.title}</h3>`;

      if (req?.prompt) {
        html += `<div style="background-color: #f8fafc; border-left: 3px solid #2ba5b5; padding: 8px 12px; margin-bottom: 12px; font-size: 10pt; color: #475569;">
          <strong>RFP Requirement:</strong> ${req.prompt}
        </div>`;
      }

      // Convert Markdown formatting (simple bold, list, paragraphs)
      const paragraphs = sec.content
        .split(/\n\n+/)
        .map((p) => {
          const formatted = p.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
          if (formatted.startsWith("- ") || formatted.startsWith("* ")) {
            const listItems = formatted
              .split(/\n[*-]\s+/)
              .map((li) => `<li>${li.replace(/^[*-]\s+/, "")}</li>`)
              .join("");
            return `<ul style="margin: 8px 0; padding-left: 24px;">${listItems}</ul>`;
          }
          return `<p style="margin: 0 0 12px 0;">${formatted}</p>`;
        })
        .join("");

      html += `${paragraphs}</div>`;
    });
  }

  // Required Forms Table
  if (requiredForms.length > 0) {
    html += `
    <div style="page-break-before: always; margin-top: 36px;">
      <h2 style="color: #0f172a; font-size: 16pt; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px;">2. Required Forms & Compliance Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 10pt;">
        <thead>
          <tr style="background-color: #f1f5f9; text-align: left;">
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Form Name</th>
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Mandatory</th>
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Status</th>
          </tr>
        </thead>
        <tbody>
          ${requiredForms
            .map(
              (f) => `
            <tr>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${f.formName}</td>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${f.mandatory ? "Yes" : "Optional"}</td>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${f.status.replace(/_/g, " ").toUpperCase()}</td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>`;
  }

  // Format Rules Table
  if (formatRules.length > 0) {
    html += `
    <div style="margin-top: 36px;">
      <h2 style="color: #0f172a; font-size: 16pt; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px;">3. Format & Submission Rules</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 10pt;">
        <thead>
          <tr style="background-color: #f1f5f9; text-align: left;">
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Rule Type</th>
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Requirement</th>
            <th style="padding: 8px 12px; border: 1px solid #cbd5e1;">Compliance Status</th>
          </tr>
        </thead>
        <tbody>
          ${formatRules
            .map(
              (r) => `
            <tr>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${r.ruleType.replace(/_/g, " ").toUpperCase()}</td>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${r.requirement}</td>
              <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${r.isCompliant ? "Compliant" : "Review Needed"}</td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>`;
  }

  html += `</div>`;
  return html;
}

/**
 * Copies proposal to clipboard as rich HTML for 1-click Google Docs / Word paste,
 * with fallback to plain-text Markdown.
 */
export async function copyProposalToGoogleDocs(compiled: CompiledProposal): Promise<boolean> {
  const html = generateGoogleDocsHtml(compiled);
  const plainText = generateMarkdownProposal(compiled);

  try {
    if (typeof ClipboardItem !== "undefined" && navigator?.clipboard?.write) {
      const htmlBlob = new Blob([html], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });
      const item = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch (err) {
    console.warn("ClipboardItem write failed, falling back to writeText:", err);
  }

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(plainText);
    return true;
  }

  return false;
}

export const copyProposalForGoogleDocs = copyProposalToGoogleDocs;

/**
 * Triggers client-side download of the proposal in Markdown format
 */
export function downloadProposalMarkdown(compiled: CompiledProposal): void {
  const md = generateMarkdownProposal(compiled);
  const safeClient = (compiled.opportunity.client || "Client").replace(/[^a-z0-9]/gi, "_");
  const safeTitle = (compiled.opportunity.title || "Proposal").replace(/[^a-z0-9]/gi, "_");
  const fileName = `${safeClient}_${safeTitle}_Proposal.md`;

  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
